import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { useEventListener } from "@tsmono/react/hooks";
import { Icon } from "@tsmono/react/icons";
import { isEditableTarget } from "@tsmono/util";

import styles from "./GoToTurnBar.module.css";

export interface GoToTurnBarHandle {
  /** Open the bar (or refocus it if already open), optionally prefilled with a
   *  turn number — the text is selected, ready to overtype. */
  open: (prefillTurn?: number) => void;
}

interface GoToTurnBarProps {
  totalTurns: number;
  /** Jump to a 1-based turn number (clamped to [1, totalTurns] before calling). */
  onJump: (turnNumber: number) => void;
  /** Sticky top offset in px (height of the chrome above, e.g. swimlanes). */
  offsetTop?: number;
  /** Ignore Ctrl/Cmd+G (e.g. while find-in-page owns the keyboard). */
  disabled?: boolean;
}

/**
 * Floating find-style "Go to turn" bar, top-center of the events column.
 * Opened by Ctrl/Cmd+G or imperatively (turn-label click, prefilled). Enter
 * clamps to the valid range and jumps via the caller's deep-link path;
 * Esc / click outside / the close button dismiss without navigating.
 */
export const GoToTurnBar = forwardRef<GoToTurnBarHandle, GoToTurnBarProps>(
  function GoToTurnBar({ totalTurns, onJump, offsetTop = 0, disabled }, ref) {
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState("");
    // Bumped on every open() so an already-open bar still refocuses/selects.
    const [focusEpoch, setFocusEpoch] = useState(0);
    const openRef = useRef(false);
    const dockRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const restoreFocusRef = useRef<HTMLElement | null>(null);
    const rangeId = useId();

    const openBar = useCallback((prefillTurn?: number) => {
      if (!openRef.current) {
        restoreFocusRef.current =
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        setValue(prefillTurn !== undefined ? String(prefillTurn) : "");
      } else if (prefillTurn !== undefined) {
        setValue(String(prefillTurn));
      }
      openRef.current = true;
      setOpen(true);
      setFocusEpoch((epoch) => epoch + 1);
    }, []);

    const close = useCallback((restoreFocus: boolean) => {
      openRef.current = false;
      setOpen(false);
      if (restoreFocus) restoreFocusRef.current?.focus();
    }, []);

    useImperativeHandle(ref, () => ({ open: openBar }), [openBar]);

    // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
    useEffect(() => {
      if (!open) return;
      inputRef.current?.focus();
      inputRef.current?.select();
    }, [open, focusEpoch]);

    // Ctrl/Cmd+G ("go to turn") — intentionally shadows the browser's
    // find-again. Ignored while the user is typing in some other field; when
    // the find band is open, `disabled` detaches this listener entirely so
    // find owns the key. Registered on window in the CAPTURE phase and
    // stopped immediately: the VS Code/Cursor webview forwards keydowns to
    // the host's keybinding service (Ctrl+G = go-to-line there), and only
    // an already-prevented, already-stopped event at the earliest phase
    // page JS can reach reliably keeps the chord in the transcript.
    useEventListener(
      disabled ? null : window,
      "keydown",
      (e) => {
        if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "g") return;
        // Several transcripts can be mounted at once (e.g. one in a modal over
        // another) — only the visible bar may claim the shortcut.
        const dock = dockRef.current;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- checkVisibility is missing in jsdom and pre-17.4 Safari despite lib.dom
        if (!dock?.isConnected || dock.checkVisibility?.() === false) return;
        // Use the shared editable check against activeElement so it pierces
        // shadow DOM (vscode-elements inputs) and covers <select> — matching
        // the other transcript keyboard handlers. Its own input is light-DOM,
        // so activeElement equals it directly for the refocus exception.
        if (
          document.activeElement !== inputRef.current &&
          isEditableTarget(document.activeElement)
        ) {
          return;
        }
        e.preventDefault();
        e.stopImmediatePropagation();
        openBar();
      },
      { capture: true }
    );

    useEventListener(open ? document : null, "pointerdown", (e) => {
      if (
        e.target instanceof Node &&
        !containerRef.current?.contains(e.target)
      ) {
        close(false);
      }
    });

    const parsed = value === "" ? undefined : parseInt(value, 10);
    const clamped =
      parsed === undefined
        ? undefined
        : Math.max(1, Math.min(totalTurns, parsed));
    const outOfRange = clamped !== undefined && clamped !== parsed;

    // Wide enough for the placeholder range or a prefilled value, so long turn
    // counts never truncate; ch + slack covers the numeric glyph width.
    const placeholder = `1–${totalTurns}`;
    const inputChars = Math.max(placeholder.length, value.length, 2);

    const commit = () => {
      if (clamped === undefined) return; // empty + Enter: no-op, keep open
      close(true);
      onJump(clamped);
    };

    // The zero-height dock stays mounted while closed: it is the visibility
    // sentinel for the keyboard listener above.
    return (
      <div ref={dockRef} className={styles.dock} style={{ top: offsetTop }}>
        {open && (
          <div
            ref={containerRef}
            className={styles.bar}
            role="dialog"
            aria-label="Go to turn"
          >
            <span className={styles.title}>Go to turn</span>
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              className={styles.input}
              style={{ width: `calc(${inputChars}ch + 16px)` }}
              aria-label="Turn number"
              aria-describedby={rangeId}
              placeholder={placeholder}
              value={value}
              onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ""))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commit();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  close(true);
                }
              }}
            />
            <span id={rangeId} className={styles.range}>
              {outOfRange
                ? `→ ${clamped} (${clamped === 1 ? "first" : "last"} turn)`
                : `/ ${totalTurns}`}
            </span>
            <span className={styles.divider} />
            <span className={styles.hint}>
              <span className={styles.hintKey}>{"↵"}</span>
              {" jump · "}
              <span className={styles.hintKey}>esc</span>
              {" close"}
            </span>
            <button
              type="button"
              className={styles.close}
              aria-label="Close"
              title="Close"
              onClick={() => close(true)}
            >
              <Icon name="close" />
            </button>
          </div>
        )}
      </div>
    );
  }
);
